import { useEffect, useId } from 'react';
import { cn } from '@/lib/utils';
import Modal from '@/components/modal/Modal.tsx';
import { ChangeDraftVm } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { ExitEditingChoice } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import ExitEditingChoices from './ExitEditingChoices';
import ExitEditingHeader from './ExitEditingHeader';
import ExitEditingImpact from './ExitEditingImpact';

interface ExitEditingModalProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  draft: ChangeDraftVm;
  onChoice: (choice: ExitEditingChoice) => void;
  /** Powrót do edycji: stopka, klawisz Escape i klik w tło. */
  onDismiss: () => void;
  /** Odrzucanie szkicu w toku — przyciski nie przyjmują drugiego kliknięcia. */
  isDiscarding?: boolean;
}

/**
 * Modal kończący edycję rezerwacji.
 *
 * Wyłączenie przełącznika „Tryb edycji” nie zamyka edycji — otwiera to okno.
 * To centralna zasada projektu: ze szkicu nie da się wyjść przypadkiem, więc
 * każde wyjście jest jawnym wyborem, a klik w tło i Escape wracają do edycji
 * zamiast cokolwiek porzucać.
 */
const ExitEditingModal = ({
  draft,
  onChoice,
  onDismiss,
  isDiscarding = false,
  className,
  ...props
}: ExitEditingModalProps) => {
  const titleId = useId();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  return (
    <Modal
      closeOnOverlayClick
      onClose={onDismiss}
      aria-labelledby={titleId}
      className={cn('max-w-[560px] rounded-2xl', className)}
      {...props}
    >
      <ExitEditingHeader titleId={titleId} draft={draft} />

      <ExitEditingImpact draft={draft} />

      <ExitEditingChoices
        itemCount={draft.itemCount}
        onChoice={onChoice}
        isDiscarding={isDiscarding}
      />

      <div className="flex justify-end border-t border-neutral-100 bg-neutral-50 px-7 py-3.5">
        <button
          type="button"
          onClick={onDismiss}
          className="cursor-pointer rounded-lg border-0 bg-transparent px-3.5 py-2 text-sm font-semibold text-neutral-600 hover:text-ink"
        >
          Wróć do edycji
        </button>
      </div>
    </Modal>
  );
};

export default ExitEditingModal;
